import React, { useCallback, useEffect, useMemo } from 'react';
import validEmail from '@hugsmidjan/qj/validEmail';
import { query } from 'api/prismic';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { Address } from 'onboarding/components/address/Address';
import { AddressPicker } from 'onboarding/components/addresspicker/AddressPicker';
import { BoxForm } from 'onboarding/components/boxForm/BoxForm';
import { Login } from 'onboarding/components/login/Login';
import { Signature } from 'onboarding/components/sign/Sign';
import { SplitLayout } from 'onboarding/components/splitlayout/SplitLayout';
import { Success } from 'onboarding/components/success/Success';
import { Summary } from 'onboarding/components/summary/Summary';
import { VideoSignIn } from 'onboarding/components/videosignin/VideoSignIn';
import { layoutQuery } from 'prismic/fragments/layoutQuery';
import { linkableFragment } from 'prismic/fragments/linkableFragment';
import { metaFragment } from 'prismic/fragments/metaFragment';
import { pageFragment } from 'prismic/fragments/pageFragment';
import { Layout, Page } from 'prismic-types';

import { Section } from 'components/section/Section';
import { PrismicMeta } from 'containers/meta/PrismicMeta';

import { useOnMount } from 'hooks/useOnMount';
import { assertNever } from 'utils/assertNever';
import { localeToPrismicLocale, PRISMIC_LOCALE } from 'utils/i18n';

import { useResize } from '../hooks/useResize';
import {
  innskraningSteps,
  InnskraningUrl,
  innskraningUrlEnum,
} from '../stores/onboarding/data';
import { useInnskraningStore } from '../stores/onboarding/useInnskraningStore';
import {
  singleNavigationQuery,
  topNavigationQuery,
} from 'prismic/fragments/navigationQuery';

const isInnskraningUrl = (url: string): url is InnskraningUrl => {
  return !!Object.values(innskraningSteps).find((value) => value.url === url);
};

const useSteps = () => {
  const isLoggedIn = useInnskraningStore(useCallback((s) => s.isLoggedIn, []));
  const user = useInnskraningStore(useCallback((s) => s.user, []));
  const selectedProperties = useInnskraningStore(
    useCallback((s) => s.selectedProperties, [])
  );

  const steps = useMemo(() => {
    return Array.from(Object.values(innskraningSteps)).filter(
      (s) =>
        s.url !== innskraningUrlEnum.SUCCESS &&
        s.url !== innskraningUrlEnum.ADD_ADDRESS
    );
  }, []);

  const getIsClickable = (url: InnskraningUrl) => {
    switch (url) {
      case innskraningUrlEnum.LOGIN:
        return true;
      case innskraningUrlEnum.ADDRESS:
        return isLoggedIn;
      case innskraningUrlEnum.ADD_ADDRESS:
        return false;
      case innskraningUrlEnum.SUMMARY:
        return !!selectedProperties.length;
      case innskraningUrlEnum.SIGNATURE:
        return !!user.email && validEmail(user.email);
      case innskraningUrlEnum.SUCCESS:
        return false;

      default:
        assertNever(url);
    }

    return true;
  };

  return steps.map((s) => {
    const isClickable = getIsClickable(s.url);

    return {
      ...s,
      isClickable,
    };
  });
};

const handleUnload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = 'Ertu viss um að þú viljir hætta?';
};

type PageProps = {
  page: Page | null;
  layout: Layout | null;
};

export default function Innskraning({
  page,
  layout,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const { isTablet } = useResize();

  const error = useInnskraningStore(useCallback((s) => s.error, []));
  const user = useInnskraningStore(useCallback((s) => s.user, []));
  const mailinglist = useInnskraningStore(
    useCallback((s) => s.mailinglist, [])
  );
  const isKassar = useInnskraningStore(useCallback((s) => s.isKassar, []));

  const steps = useSteps();

  const allProperties = useInnskraningStore(
    useCallback((s) => s.selectedProperties, [])
  );
  const selectedProperties = useInnskraningStore(
    useCallback((s) => s.selectedProperties.filter((p) => p.selected), [])
  );
  const propertyDelivered = useInnskraningStore(
    useCallback((s) => s.propertyDelivered, [])
  );
  const addresses = useInnskraningStore(useCallback((s) => s.addresses, []));
  const offerType = useInnskraningStore(useCallback((s) => s.offerType, []));

  const videoConfig = useInnskraningStore(
    useCallback((s) => s.videoConfig, [])
  );

  const {
    clearError,
    setError,
    setSelectedProperties,
    setPropertyDelivered,
    setScene,
    setUser,
    setMailinglist,
    setIsKassar,
    setAddresses,
    setOfferType,
  } = useInnskraningStore(
    useCallback(
      (s) => ({
        clearError: s.clearError,
        setError: s.setError,
        setSelectedProperties: s.setSelectedProperties,
        setPropertyDelivered: s.setPropertyDelivered,
        setScene: s.setScene,
        setUser: s.setUser,
        setMailinglist: s.setMailinglist,
        setIsKassar: s.setIsKassar,
        setAddresses: s.setAddresses,
        setOfferType: s.setOfferType,
      }),
      []
    )
  );

  const formType = router.query.type as string | undefined;
  const isPowerPlan = router.query.uid as
    | 'sparorka_data'
    | 'bilaorka_data'
    | 'hledsluorka_data'
    | 'naeturorka_data'
    | 'graenn_data'
    | undefined;

  if (!isKassar && formType === 'kassar') {
    setIsKassar(true);
  } else if (isKassar && formType === 'kiv') {
    setIsKassar(false);
  }

  useEffect(() => {
    if (formType) {
      setOfferType(formType);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]);

  useEffect(() => {
    if (isPowerPlan) {
      const powerPlanMap = {
        sparorka_data: 'sparorka',
        graenn_data: 'graenorka',
        naeturorka_data: 'naeturorka',
        bilaorka_data: 'bilaorka',
        hledsluorka_data: 'hledsluorka',
      };
      setOfferType(powerPlanMap[isPowerPlan]);
    } else if (
      formType &&
      [
        'sparorka',
        'graenorka',
        'naeturorka',
        'bilaorka',
        'hledsluorka',
      ].includes(formType)
    ) {
      setOfferType(formType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPowerPlan]);

  const currentStep = useMemo(() => {
    if (isInnskraningUrl(router.asPath)) {
      return innskraningSteps[router.asPath];
    } else {
      return innskraningSteps[innskraningUrlEnum.LOGIN];
    }
  }, [router.asPath]);

  useEffect(() => {
    if (router.asPath !== innskraningUrlEnum.LOGIN) {
      addEventListener('beforeunload', handleUnload);
      return () => removeEventListener('beforeunload', handleUnload);
    }
  }, [router.asPath]);

  const renderCurrentStep = () => {
    switch (router.asPath) {
      case innskraningUrlEnum.LOGIN:
        return (
          // orkusalan.is/innskraning

          <Login
            clearError={clearError}
            error={error}
            onSubmit={() => {
              setScene('transitionToRoomba', isKassar);
              router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.ADDRESS);
            }}
            setError={setError}
            setProperties={setSelectedProperties}
            setAddresses={setAddresses}
            setUser={setUser}
            isKassar={isKassar}
            offerType={offerType}
          />
        );

      case innskraningUrlEnum.ADDRESS:
        if (isKassar) {
          return (
            <BoxForm
              userName={user.name}
              addresses={addresses}
              setAddresses={setAddresses}
              setProperties={setSelectedProperties}
              setPropertyDelivered={setPropertyDelivered}
              onSubmit={() => {
                setScene('addressConfirm', isKassar);
                router.push(
                  innskraningUrlEnum.LOGIN,
                  innskraningUrlEnum.SUMMARY
                );
              }}
            />
          );
        }
        return (
          // orkusalan.is/innskraning?skref=heimilisfang
          <Address
            onSubmit={() => {
              setScene('addressConfirm', isKassar);
              router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.SUMMARY);
            }}
            properties={allProperties}
            setProperties={setSelectedProperties}
            transitionToAddNewAddress={() => {
              setScene('transitionToAddressPicker', isKassar);
              router.push(
                innskraningUrlEnum.LOGIN,
                innskraningUrlEnum.ADD_ADDRESS
              );
            }}
            user={user}
            offerType={offerType}
          />
        );

      case innskraningUrlEnum.ADD_ADDRESS:
        return (
          // orkusalan.is/innskraning?skref=baeta-vid-heimilisfangi

          <AddressPicker
            onSubmit={() => {
              setScene('roombaExitLoop', isKassar);
              router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.ADDRESS);
            }}
            properties={allProperties}
            setProperties={setSelectedProperties}
            onIsLoading={(isLoading) => {
              if (isLoading) {
                setScene('roombaStartLoop', isKassar);
              } else {
                setScene('addressPickerResults', isKassar);
              }
            }}
          />
        );

      case innskraningUrlEnum.SUMMARY:
        // orkusalan.is/innskraning?skref=samantekt

        return (
          <Summary
            onSubmit={() => {
              setScene('transitionToNight', isKassar);
              router.push(
                innskraningUrlEnum.LOGIN,
                innskraningUrlEnum.SIGNATURE
              );
            }}
            properties={selectedProperties}
            addresses={addresses}
            user={user}
            setUser={setUser}
            mailinglist={mailinglist}
            setMailinglist={setMailinglist}
            isKassar={isKassar}
          />
        );

      case innskraningUrlEnum.SIGNATURE:
        return (
          // orkusalan.is/innskraning?skref=undirskrift

          <Signature
            selectedPropertiesArray={selectedProperties}
            user={user}
            onSubmit={() => {
              if (offerType !== 'svellid') {
                setScene('signatureComplete', isKassar);
              }
              router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.SUCCESS);
            }}
            propertyDelivered={propertyDelivered}
            mailinglist={mailinglist}
            isKassar={isKassar}
            addresses={addresses}
            offerType={offerType}
          />
        );

      case innskraningUrlEnum.SUCCESS:
        // orkusalan.is/innskraning?skref=til-hamingju

        return <Success isKassar={isKassar} offerType={offerType} />;

      default:
        break;
    }
  };

  useOnMount(() => {
    if (router.asPath !== innskraningUrlEnum.LOGIN) {
      router.push(innskraningUrlEnum.LOGIN);
    }
  });

  const isFinished = currentStep.url === innskraningUrlEnum.SUCCESS;

  const image =
    !isKassar && offerType === 'svellid'
      ? 'https://images.prismic.io/orkusalan/0c2ef4b5-a0a3-423b-a594-fa497e524f90_ORK_Studsvellid_Vefur_1200x1200_1112+%281%29.png'
      : undefined;

  // Wait with rendering until we know if we are on mobile or not
  // to avoid errors in mobile safari
  return isTablet !== undefined && router.isReady ? (
    <>
      {page && layout && <PrismicMeta data={page} layout={layout} />}
      <Section container>
        <SplitLayout
          title={isFinished ? currentStep.text : undefined}
          steps={isFinished ? undefined : steps} // Dont show on success step
          currentStep={isFinished ? undefined : currentStep.number}
          image={image}
          video={
            isTablet || image ? undefined : (
              <VideoSignIn
                src={
                  isKassar
                    ? '/videos/orkusalan_kassi_stofa_lowres.mp4'
                    : '/videos/orkusalan_hus_stofa_lowres.mp4'
                }
                {...videoConfig}
              />
            )
          }
          goBack={
            currentStep.url === innskraningUrlEnum.ADD_ADDRESS
              ? () =>
                  router.push(
                    innskraningUrlEnum.LOGIN,
                    innskraningUrlEnum.ADDRESS
                  )
              : undefined
          }
        >
          {renderCurrentStep()}
        </SplitLayout>
      </Section>
    </>
  ) : null;
}

// export default dynamic(() => Promise.resolve(Innskraning));

export const pageQuery = `
  query page($uid: String!, $lang: String = "${PRISMIC_LOCALE}") {
    page(uid: $uid, lang: $lang) {
      ...page
    }
  }
  ${pageFragment}
  ${metaFragment}
  ${linkableFragment}
`;

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  preview = false,
  locale,
  previewData,
}) => {
  const lang = localeToPrismicLocale(locale);
  const uid = 'velkomin-i-vidskipti';
  const variables = { uid, lang };
  const cacheKey = `${lang}-uid-${uid}`;

  // GraphQL hack for deep navigation
  const topNavData = await query(topNavigationQuery, { variables: { lang } });
  const topNav =
    topNavData?.allNavigations.edges
      ?.filter((edge) => edge && edge.node)
      .map((edge) => {
        return edge?.node._meta.uid;
      }) ?? [];

  const megamenuItems = async () => {
    return await Promise.all(
      topNav.map((uid) =>
        query(singleNavigationQuery, { variables: { lang, uid: uid! } })
      )
    );
  };

  const [navData, layoutData, pageData] = await Promise.all([
    megamenuItems(),
    query(layoutQuery, {
      variables: { lang },
      cacheKey: `layout-${lang}`,
    }),
    query(pageQuery, {
      previewData,
      variables,
      cacheKey: `page-${cacheKey}`,
      breadcrumbs: [uid],
    }),
  ]);

  const layout = layoutData?.layout ?? null;
  const page = pageData?.page ?? null;
  const navigation = navData.map((navDataItem) => navDataItem?.navigation);

  if (!page) {
    throw Error('Erro fetching page velkomin-i-vidskipti, page was null');
  }

  return {
    props: {
      preview,
      page,
      navigation,
      layout,
    },
    // revalidate: 60 * 10,
  };
};
